import { AfterViewInit, ChangeDetectorRef, Component, Inject, inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LegalService } from '../../../../services/legal.service';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { finalize, map, skip, startWith, switchMap, take, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { AuthAdaptorService, AuthStateAdaptorService } from '@aiwyn/auth';
import {
  AutoPay,
  AutoPaymentPlanRequest,
  CreateAutoPaymentPlanRequest,
  PaymentPlan,
  PaymentPlanApiService,
  UpdateAutoPaymentPlanRequest,
} from '@aiwyn/api';
import { DatePipe } from '@angular/common';
import { ConfigService } from '@aiwyn/config';
import { formatAutoPayDate } from '../../autopay-format.utils';
import {
  AbstractPaymentMethod,
  AutoPaymentPlanTrackService,
  AutoPaymentPlanWorkflowEvent,
  PaymentFlow,
  PaymentMethodIntent,
  PaymentMethodMode,
  PaymentPlatformService,
  PaymentsWorkflowType,
  RecipientEmailsComponent,
  SelectPaymentMethodComponent,
} from '@aiwyn/common/payments';
import {
  daysToMilliseconds,
  DialogSize,
  extractFailureContext,
  getBeginningOfDay,
  ignoreFalsy,
  ignoreNullish,
  Logger,
  mapToAndResult,
} from '@aiwyn/shared';

/**
 * Populate the dialog with the required data. autoPay should only be populated if it is an edit
 */
export interface AddEditAutopayDialogData {
  isEnabled?: boolean;
  account: {
    id: number,
    name: string,
  };
  didCreate?: boolean;
  autoPay?: AutoPay;
  recurringPaymentPlans: PaymentPlan[];
}

type UserActionAndIdKey = keyof Pick<
  CreateAutoPaymentPlanRequest & UpdateAutoPaymentPlanRequest,
  'createdByStaffId' | 'createdByContactId' | 'lastUpdatedByStaffId' | 'lastUpdatedByContactId'
>;

@Component({
  selector: 'app-add-edit-autopay-dialog',
  templateUrl: './add-edit-autopay-dialog.component.html',
  styleUrls: ['./add-edit-autopay-dialog.component.scss'],
  standalone: false,
  providers: [AutoPaymentPlanTrackService],
})
export class AddEditAutopayDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  // Set to true once a terminal AUTO_PAYMENT_PLAN_* event has fired (CREATE_SUCCEEDED, UPDATE_SUCCEEDED).
  // afterClosed reads this to decide whether to emit AUTO_PAYMENT_PLAN_CANCELLED.
  private _didEmitTerminalEvent = false;

  private readonly autoPaymentPlanTrackService = inject(AutoPaymentPlanTrackService);

  constructor(
    public readonly dialogRef: MatDialogRef<AddEditAutopayDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: AddEditAutopayDialogData,
    public readonly legalService: LegalService,
    public readonly authAdaptor: AuthAdaptorService,
    private readonly paymentPlanApiService: PaymentPlanApiService,
    private readonly datePipe: DatePipe,
    public readonly configService: ConfigService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly authStateAdaptor: AuthStateAdaptorService,
    private readonly logger: Logger,
  ) {}

  ngOnInit(): void {
    this.prepopulateForm(this.data);

    if (!this.data.didCreate) {
      this.autoPayGroupV5.disable({ emitEvent: false });
    }

    const existingPlanId = this.data.autoPay?.autoPayId;
    if (existingPlanId != undefined) {
      this.autoPaymentPlanTrackService.trackWorkflow(
        AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_UPDATE_STARTED,
        { planId: String(existingPlanId) },
      );
    } else {
      this.autoPaymentPlanTrackService.trackWorkflow(AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_CREATE_STARTED);
    }

    // Catches every dismiss path (Cancel button, X, ESC, backdrop) in one place.
    this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (!this._didEmitTerminalEvent) {
        this.autoPaymentPlanTrackService.trackWorkflow(AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_CANCELLED);
      }
    });
  }

  ngAfterViewInit(): void {
    // canAddAdditionalEmail$ updates while the view is being checked, mark for change detection on view init
    this.changeDetectorRef.detectChanges();
  }

  private _unsubscribe$: Subject<void> = new Subject();
  private _didUpdatePaymentMethod$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  didUpdatePaymentMethod$: Observable<boolean> = this._didUpdatePaymentMethod$.asObservable();
  private readonly _isProcessingRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isProcessingRequest$: Observable<boolean> = this._isProcessingRequest$.asObservable();
  private readonly _didRequestError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  didRequestError$: Observable<boolean> = this._didRequestError$.asObservable();

  readonly DialogSize = DialogSize;
  readonly PaymentMethodIntent = PaymentMethodIntent;
  readonly PaymentFlow = PaymentFlow;
  readonly PaymentsWorkflowType = PaymentsWorkflowType;

  @ViewChildren(SelectPaymentMethodComponent) selectPaymentMethodComponent: QueryList<SelectPaymentMethodComponent>;
  @ViewChildren(RecipientEmailsComponent) recipientEmailsComponent: QueryList<RecipientEmailsComponent>;

  paymentMethodMode$ = new BehaviorSubject<PaymentMethodMode>(undefined);
  selectedPaymentMethodId$ = new BehaviorSubject<string>(this.data.autoPay?.active ? this.data.autoPay.method?.paymentMethodId : undefined);
  savedPaymentMethodsExist$ = this.paymentPlatformService.getSavedPaymentMethodsExist().pipe(
    tap(savedPaymentMethodsExist => {
      if (savedPaymentMethodsExist && this.data.didCreate) {
        this.paymentMethodMode$.next(PaymentMethodMode.SAVED);
      }
    }),
  );

  dayOptions: { value: any, display: string }[] = [
    ...Array.from({ length: 28 }).map((_, index) => ({ value: index + 1, display: moment.localeData().ordinal(index + 1) })),
    { value: 'last', display: 'Last' },
  ];

  /**
   * Form Inputs
   */
  deactivateRecurringPaymentsControl: UntypedFormControl = new UntypedFormControl(
    // If there are no recurring plans, the checkbox will be unchecked, hidden, and have no validation. Otherwise, it's shown and required.
    ...(this.data.recurringPaymentPlans.length === 0 ? [false] : [true, Validators.requiredTrue]),
  );
  dayControl: UntypedFormControl = new UntypedFormControl(1, Validators.required);
  maxAmountControl: UntypedFormControl = new UntypedFormControl(undefined, [Validators.min(0), Validators.max(9999999.99), Validators.maxLength(10)]);
  autoPayGroupV5: UntypedFormGroup = new UntypedFormGroup(
    {
      day: this.dayControl,
      maxAmount: this.maxAmountControl,
      deactivateRecurringPayments: this.deactivateRecurringPaymentsControl,
    },
    { updateOn: 'blur' },
  );

  isAutoPayFormEnabled$ = this.selectedPaymentMethodId$.pipe(
    skip(1),
    ignoreNullish(),
    map(_ => true),
    startWith(this.data.didCreate || !this.data.isEnabled || this.authAdaptor.isAdmin),
    tap(shouldEnable => {
      const toggleFn: keyof UntypedFormControl & ('disable' | 'enable') = shouldEnable ? 'enable' : 'disable';
      this.autoPayGroupV5[toggleFn]({ emitEvent: false });
    }),
    takeWhile(Boolean), // should just stop listening if form is already enabled (can't go from enabled to disabled)
    takeUntil(this._unsubscribe$),
  );

  primaryEmail = this.authStateAdaptor.getUserEmailSnapshot();
  onPrimaryEmailChanged(email: string): void {
    this.primaryEmail = email;
  }

  additionalEmails: string[] = [];
  onAdditionalEmailsChanged(emails: string[]): void {
    this.additionalEmails = emails;
  }

  onSavedPaymentMethodSelected(selectedPaymentMethod: AbstractPaymentMethod): void {
    this.selectedPaymentMethodId$.next(selectedPaymentMethod.id);
    // AUTO_PAYMENT_PLAN_PAYMENT_METHOD_SELECTED is dispatched by SelectPaymentMethodComponent
    // (workflow=AUTO_PAYMENT_PLAN). Don't re-emit it here.
    this.changeDetectorRef.detectChanges();
  }

  prepopulateForm(data: AddEditAutopayDialogData): void {
    if (data.isEnabled) {
      const [, , day] = data.autoPay.startDate.split('-').map(stringPart => +stringPart);
      this.dayControl.setValue(day > 28 ? 'last' : day);
      this.maxAmountControl.setValue(data.autoPay?.amount || undefined, { emitEvent: false });
    }
  }

  validate(): Observable<boolean> {
    this.autoPayGroupV5.markAllAsTouched();
    this.autoPayGroupV5.markAsDirty();

    if (this.autoPayGroupV5.invalid) {
      return of(false);
    }

    return combineLatest([
      this.selectPaymentMethodComponent.first.validate(),
      this.recipientEmailsComponent.first.validate(),
    ]).pipe(
      mapToAndResult(),
    );
  }

  onSave(): void {
    this._isProcessingRequest$.next(true);

    const isUpdate = this.data.autoPay?.autoPayId != undefined;
    const operationKey = 'AUTO_PAYMENT_PLAN_SAVE';

    this.validate().pipe(
      take(1),
      ignoreFalsy(),
      tap(_ => this._didRequestError$.next(false)),
      tap(_ => {
        this.autoPaymentPlanTrackService.trackWorkflow(
          AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_SUBMITTED,
          {
            dayOfMonth: String(this.dayControl.value),
            maxAmount: +(this.maxAmountControl.value ?? '') || undefined,
          },
        );
        this.autoPaymentPlanTrackService.startOperation(operationKey);
      }),
      // ensure autopay plan can be created (can only have one active payment plan on a client account)
      switchMap(_ => this.deactivatePaymentPlansIfApplicable()),
      // build save payload
      switchMap(_ => this.paymentPlatformService.getSavedPaymentMethod(this.selectedPaymentMethodId$.value)),
      map(selectedPaymentMethod => {
        const startDate = AddEditAutopayDialogComponent.getStartDate(
          this.data.autoPay?.startDate,
          this.dayControl.value,
          this.configService.autopayBufferDays,
        );
        const formattedStartDate = formatAutoPayDate(this.datePipe, startDate);

        const recipientEmails = [this.primaryEmail].concat(this.additionalEmails);
        return AddEditAutopayDialogComponent.getSaveAutoPayPlanPayload(
          this.data,
          selectedPaymentMethod,
          formattedStartDate,
          recipientEmails,
          this.maxAmountControl.value,
          this.authAdaptor.isAdmin,
          this.authAdaptor.isAdmin ? this.authAdaptor.getStaffId : this.authAdaptor.getContactIdOrNull,
        );
      }),
      // execute save
      switchMap(saveAutoPayPlanPayload =>
        isUpdate
          ? this.paymentPlanApiService.updateAutoPaymentPlan(saveAutoPayPlanPayload as UpdateAutoPaymentPlanRequest)
          : this.paymentPlanApiService.createAutoPaymentPlan(saveAutoPayPlanPayload as CreateAutoPaymentPlanRequest),
      ),
      take(1),
      tap({
        next: response => {
          this._didEmitTerminalEvent = true;
          const operationDurationMs = this.autoPaymentPlanTrackService.endOperation(operationKey);
          if (isUpdate) {
            this.autoPaymentPlanTrackService.trackWorkflow(
              AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_UPDATE_SUCCEEDED,
              { planId: String(this.data.autoPay.autoPayId), operationDurationMs },
            );
          } else {
            const createdId = (response as { data?: { CreateAutoPaymentPlan?: { id?: number | string } } })
              ?.data?.CreateAutoPaymentPlan?.id;
            // createdId should always be defined when createAutoPaymentPlan succeeds; the empty-string fallback
            // exists only as a defensive measure if the API ever returns success without an id.
            if (createdId == undefined) {
              this.logger.warn('AUTO_PAYMENT_PLAN_CREATE_SUCCEEDED fired with empty planId — API succeeded without returning an id');
            }
            this.autoPaymentPlanTrackService.trackWorkflow(
              AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_CREATE_SUCCEEDED,
              { planId: createdId != undefined ? String(createdId) : '', operationDurationMs },
            );
          }
          this.dialogRef.close(true);
        },
        error: error => {
          this._didRequestError$.next(true);
          const operationDurationMs = this.autoPaymentPlanTrackService.endOperation(operationKey);
          const failureContext = extractFailureContext(error);
          if (isUpdate) {
            this.autoPaymentPlanTrackService.trackWorkflow(
              AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_UPDATE_FAILED,
              { ...failureContext, planId: String(this.data.autoPay.autoPayId), operationDurationMs },
            );
          } else {
            this.autoPaymentPlanTrackService.trackWorkflow(
              AutoPaymentPlanWorkflowEvent.AUTO_PAYMENT_PLAN_CREATE_FAILED,
              { ...failureContext, operationDurationMs },
            );
          }
        },
      }),
      takeUntil(this._unsubscribe$),
      finalize(() => this._isProcessingRequest$.next(false)),
    ).subscribe();
  }

  static deactivateRecurringPaymentPlans(
    recurringPaymentPlans: PaymentPlan[],
    deactivationRequest: (recurringPaymentPlan: PaymentPlan) => Observable<unknown>,
  ): Observable<unknown> {
    if ((recurringPaymentPlans ?? []).length === 0) {
      return of(undefined);
    }

    const deactivateRecurringPaymentPlanRequests = recurringPaymentPlans.map(paymentPlan => deactivationRequest(paymentPlan));
    return combineLatest(deactivateRecurringPaymentPlanRequests);
  }

  static getStartDate(activePlanStartDate: string, selectedDayOfMonth: string, autoPayBufferDays: number): Date {
    const today = new Date();
    const isAutoPayStartDateInFuture = new Date(activePlanStartDate ?? 0).getTime() > today.getTime();
    const startDate: Date = getBeginningOfDay(isAutoPayStartDateInFuture ? activePlanStartDate : today.getTime());

    // Calculate the last day of the month if selected
    if (selectedDayOfMonth === 'last') {
      startDate.setMonth(startDate.getMonth() + 1);
      startDate.setDate(-1);
    } else {
      // else just use the number
      startDate.setDate(+selectedDayOfMonth);
    }

    // Cannot have a date in the past
    if (startDate.getTime() <= today.getTime()) {
      startDate.setMonth(startDate.getMonth() + 1);

      // Check that the start date is not after the expanded buffer range
      const bufferMilliseconds = daysToMilliseconds(autoPayBufferDays);
      const bufferDate = getBeginningOfDay(startDate.getTime() - bufferMilliseconds);

      if (bufferDate.getTime() < today.getTime()) {
        startDate.setMonth(startDate.getMonth() + 1);
      }
    }

    return startDate;
  }

  static getSaveAutoPayPlanPayload(
    autoPayData: AddEditAutopayDialogData,
    selectedPaymentMethod: AbstractPaymentMethod,
    startDate: string,
    recipientEmails: string[],
    maxPaymentAmount: string,
    isAdmin: boolean,
    userId: number,
  ): AutoPaymentPlanRequest {
    // TODO REFACTOR: Is billingDetails always present?
    const { state, postalCode: zip } = selectedPaymentMethod.billingDetails?.address ?? {};
    const country = selectedPaymentMethod.country;
    const userActionAndIdKey = AddEditAutopayDialogComponent.getUserActionAndIdKey(isAdmin, !!autoPayData.autoPay?.autoPayId);

    return {
      id: autoPayData.autoPay?.autoPayId,
      clientId: autoPayData.account.id,
      last4: selectedPaymentMethod.lastFour,
      // set as subtype for cards, type for bank accounts
      paymentMethod: (selectedPaymentMethod.subType ?? selectedPaymentMethod.type).toUpperCase(),
      paymentMethodId: selectedPaymentMethod.id,
      startDate,
      active: true,
      cardType: selectedPaymentMethod.brand,
      country,
      state,
      zip,
      emailConfirmationAddressList: recipientEmails,
      maxPaymentAmount: +(maxPaymentAmount ?? '') || undefined, // +'' === 0
      [userActionAndIdKey]: userId,
    };
  }

  static getUserActionAndIdKey(isAdmin: boolean, isUpdateRequest: boolean): UserActionAndIdKey {
    if (isAdmin) {
      return isUpdateRequest ? 'lastUpdatedByStaffId' : 'createdByStaffId';
    }

    return isUpdateRequest ? 'lastUpdatedByContactId' : 'createdByContactId';
  }

  deactivatePaymentPlansIfApplicable(): Observable<unknown[]> {
    return this.data.recurringPaymentPlans.length > 0
      ? combineLatest(this.data.recurringPaymentPlans.map(paymentPlan => this.paymentPlanApiService.deactivatePaymentPlan(paymentPlan)))
      : of(undefined);
  }

  /**
   * Validate that all the additional email controls in a given group are valid
   * @param additionalEmailControls
   */
  static areAllAdditionalEmailControlsValid(additionalEmailControls: AbstractControl[]): boolean {
    return !additionalEmailControls.length || additionalEmailControls.every(control => control.value && control.valid);
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
